/**
 * FDD-specific DOCX + PDF exporters. Reuses the same visual language as
 * the proposal exporter (cover page, TOC, headers/footers, signature
 * page) but with FDD sections and an "FDD" document title.
 */

import path from "path";
import { fileURLToPath } from "url";
import fs from "fs";
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, Header, Footer,
  PageNumber, AlignmentType, PageBreak,
} from "docx";
import { createRequire } from "module";
import { FDD_SECTIONS } from "./fdd.js";

const _require = createRequire(import.meta.url);
const PdfPrinter: new (fonts: any, vfs: any, urlResolver: any, localAccess: any) => any =
  (_require("pdfmake/js/Printer.js").default ?? _require("pdfmake/js/Printer.js"));
const _vfs: any = _require("pdfmake/js/virtual-fs.js").default ?? _require("pdfmake/js/virtual-fs.js");
const URLResolver: new (fs: any) => any =
  (_require("pdfmake/js/URLResolver.js").default ?? _require("pdfmake/js/URLResolver.js"));

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);
const FONTS_DIR = path.join(__dirname, "../../fonts");

export async function generateFddDocx(
  fdd: Record<string, string>,
  tenderTitle: string,
  companyName: string,
  language: "ar" | "en",
): Promise<Buffer> {
  const isAr = language === "ar";
  const docTitle = isAr ? "وثيقة التصميم الوظيفي" : "Functional Design Document";
  const dateStr = new Date().toLocaleDateString(isAr ? "ar-SA" : "en-US");
  const children: Paragraph[] = [];

  // Cover
  for (let i = 0; i < 6; i++) children.push(new Paragraph({ text: "" }));
  children.push(new Paragraph({
    children: [new TextRun({ text: docTitle, bold: true, size: 72, color: "1a3a5c", font: "Arial" })],
    alignment: AlignmentType.CENTER,
  }));
  children.push(new Paragraph({ text: "" }));
  children.push(new Paragraph({
    children: [new TextRun({ text: tenderTitle, size: 36, color: "333333", font: "Arial" })],
    alignment: AlignmentType.CENTER,
  }));
  children.push(new Paragraph({ text: "" }));
  children.push(new Paragraph({
    children: [new TextRun({ text: isAr ? `مقدّمة من` : `Prepared by`, size: 22, color: "888888", font: "Arial" })],
    alignment: AlignmentType.CENTER,
  }));
  children.push(new Paragraph({
    children: [new TextRun({ text: companyName, size: 28, bold: true, color: "1a3a5c", font: "Arial" })],
    alignment: AlignmentType.CENTER,
  }));
  children.push(new Paragraph({ text: "" }));
  children.push(new Paragraph({
    children: [new TextRun({ text: isAr ? `التاريخ: ${dateStr}` : `Date: ${dateStr}`, size: 22, color: "555555", font: "Arial" })],
    alignment: AlignmentType.CENTER,
  }));
  children.push(new Paragraph({ children: [new PageBreak()] }));

  // TOC
  children.push(new Paragraph({
    children: [new TextRun({ text: isAr ? "جدول المحتويات" : "Table of Contents", bold: true, size: 36, color: "1a3a5c", font: "Arial" })],
    heading: HeadingLevel.HEADING_1,
    alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
    bidirectional: isAr,
  }));
  for (const [idx, s] of FDD_SECTIONS.entries()) {
    children.push(new Paragraph({
      children: [new TextRun({ text: `${idx + 1}. ${isAr ? s.arLabel : s.enLabel}`, size: 24, font: "Arial" })],
      alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
      bidirectional: isAr,
    }));
  }
  children.push(new Paragraph({ children: [new PageBreak()] }));

  // Body sections
  for (const [idx, s] of FDD_SECTIONS.entries()) {
    const baseLabel = isAr ? s.arLabel : s.enLabel;
    children.push(new Paragraph({
      children: [new TextRun({ text: `${idx + 1}. ${baseLabel}`, bold: true, size: 32, color: "1a3a5c", font: "Arial" })],
      heading: HeadingLevel.HEADING_1,
      alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
      bidirectional: isAr,
    }));
    const content = fdd[s.key] || "";
    if (!content) {
      children.push(new Paragraph({
        children: [new TextRun({ text: isAr ? "(لا يوجد محتوى)" : "(No content)", italics: true, color: "999999", size: 22, font: "Arial" })],
        alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
        bidirectional: isAr,
      }));
    } else {
      let subNum = 0;
      for (const rawLine of content.split("\n")) {
        const line = rawLine.trim();
        if (!line) { children.push(new Paragraph({ text: "" })); continue; }
        if (/^##\s+/.test(line)) {
          subNum += 1;
          const subTitle = line.replace(/^##\s+/, "");
          children.push(new Paragraph({
            children: [new TextRun({ text: `${idx + 1}.${subNum} ${subTitle}`, bold: true, size: 26, color: "2a4a7a", font: "Arial" })],
            heading: HeadingLevel.HEADING_2,
            alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
            bidirectional: isAr,
          }));
          continue;
        }
        const bullet = /^[-•*]\s+/.test(line);
        const text = bullet ? line.replace(/^[-•*]\s+/, "") : line;
        children.push(new Paragraph({
          children: [new TextRun({ text, size: 22, font: "Arial" })],
          alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
          bidirectional: isAr,
          ...(bullet ? { bullet: { level: 0 } } : {}),
        }));
      }
    }
    children.push(new Paragraph({ text: "" }));
  }

  const header = new Header({
    children: [new Paragraph({
      children: [new TextRun({ text: `${companyName}  —  ${docTitle}`, size: 16, color: "666666", font: "Arial" })],
      alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
      bidirectional: isAr,
    })],
  });
  const footer = new Footer({
    children: [new Paragraph({
      children: [
        new TextRun({ text: `${dateStr}  •  `, size: 16, color: "888888", font: "Arial" }),
        new TextRun({ text: isAr ? "صفحة " : "Page ", size: 16, color: "888888", font: "Arial" }),
        new TextRun({ children: [PageNumber.CURRENT], size: 16, color: "888888", font: "Arial" }),
      ],
      alignment: AlignmentType.CENTER,
    })],
  });

  const doc = new Document({
    sections: [{ headers: { default: header }, footers: { default: footer }, children }],
  });
  return await Packer.toBuffer(doc);
}

export async function generateFddPdf(
  fdd: Record<string, string>,
  tenderTitle: string,
  companyName: string,
  language: "ar" | "en",
): Promise<Buffer> {
  const isAr = language === "ar";
  const docTitle = isAr ? "وثيقة التصميم الوظيفي" : "Functional Design Document";
  const dateStr = new Date().toLocaleDateString(isAr ? "ar-SA" : "en-US");
  const ALIGN = isAr ? "right" : "left";

  const fontTtfPath = path.join(FONTS_DIR, "Cairo-Regular.ttf");
  const fontBuffer = fs.existsSync(fontTtfPath) ? fs.readFileSync(fontTtfPath) : null;
  const fontDef = fontBuffer
    ? { Cairo: { normal: fontBuffer, bold: fontBuffer, italics: fontBuffer, bolditalics: fontBuffer } }
    : { Roboto: { normal: "Helvetica", bold: "Helvetica-Bold", italics: "Helvetica-Oblique", bolditalics: "Helvetica-BoldOblique" } };
  const mainFont = fontBuffer ? "Cairo" : "Roboto";
  const urlResolver = new URLResolver(_vfs);
  urlResolver.setUrlAccessPolicy(() => false);
  const printer = new PdfPrinter(fontDef, _vfs, urlResolver, () => true);

  const content: any[] = [];
  // Cover
  content.push({ text: "", margin: [0, 100, 0, 0] });
  content.push({ text: docTitle, fontSize: 32, bold: true, color: "#1a3a5c", alignment: "center", margin: [0, 60, 0, 20] });
  content.push({
    canvas: [{ type: "line", x1: 100, y1: 0, x2: 375, y2: 0, lineWidth: 2, lineColor: "#1a3a5c" }],
    margin: [0, 0, 0, 20],
  });
  content.push({ text: tenderTitle, fontSize: 18, color: "#333333", alignment: "center", margin: [0, 0, 0, 30] });
  content.push({ text: isAr ? `مقدّمة من` : `Prepared by`, fontSize: 11, color: "#888888", alignment: "center", margin: [0, 30, 0, 4] });
  content.push({ text: companyName, fontSize: 16, bold: true, color: "#1a3a5c", alignment: "center", margin: [0, 0, 0, 30] });
  content.push({ text: isAr ? `التاريخ: ${dateStr}` : `Date: ${dateStr}`, fontSize: 12, color: "#555555", alignment: "center" });
  content.push({ text: "", pageBreak: "after" });

  // TOC
  content.push({ text: isAr ? "جدول المحتويات" : "Table of Contents", fontSize: 20, bold: true, color: "#1a3a5c", alignment: ALIGN, margin: [0, 0, 0, 12] });
  content.push({ canvas: [{ type: "line", x1: 0, y1: 0, x2: 475, y2: 0, lineWidth: 1, lineColor: "#1a3a5c" }], margin: [0, 0, 0, 14] });
  for (const [idx, s] of FDD_SECTIONS.entries()) {
    content.push({
      text: `${idx + 1}. ${isAr ? s.arLabel : s.enLabel}`,
      fontSize: 12, color: "#333333", alignment: ALIGN, margin: [10, 4, 10, 4],
    });
  }
  content.push({ text: "", pageBreak: "after" });

  // Body
  for (const [idx, s] of FDD_SECTIONS.entries()) {
    const baseLabel = isAr ? s.arLabel : s.enLabel;
    content.push({ text: `${idx + 1}. ${baseLabel}`, fontSize: 15, bold: true, color: "#1a3a5c", alignment: ALIGN, margin: [0, 12, 0, 4] });
    content.push({ canvas: [{ type: "line", x1: 0, y1: 0, x2: 160, y2: 0, lineWidth: 1.5, lineColor: "#1a3a5c" }], margin: [0, 0, 0, 8] });
    const raw = fdd[s.key] || "";
    if (!raw) {
      content.push({ text: isAr ? "(لا يوجد محتوى)" : "(No content)", italics: true, color: "#999999", fontSize: 11, alignment: ALIGN });
      continue;
    }
    let subNum = 0;
    const blocks: any[] = [];
    const bulletItems: string[] = [];
    const flushBullets = () => {
      if (bulletItems.length === 0) return;
      blocks.push({ ul: bulletItems.slice(), margin: [10, 0, 10, 6], fontSize: 11, color: "#222222", alignment: ALIGN });
      bulletItems.length = 0;
    };
    for (const rawLine of raw.split("\n")) {
      const line = rawLine.trim();
      if (!line) { flushBullets(); continue; }
      if (/^##\s+/.test(line)) {
        flushBullets();
        subNum += 1;
        blocks.push({ text: `${idx + 1}.${subNum} ${line.replace(/^##\s+/, "")}`, bold: true, fontSize: 13, color: "#2a4a7a", margin: [0, 8, 0, 4], alignment: ALIGN });
        continue;
      }
      if (/^[-•*]\s+/.test(line)) {
        bulletItems.push(line.replace(/^[-•*]\s+/, ""));
        continue;
      }
      flushBullets();
      blocks.push({ text: line, fontSize: 11, color: "#222222", alignment: ALIGN, lineHeight: 1.5, margin: [0, 0, 0, 4] });
    }
    flushBullets();
    content.push(...blocks);
    content.push({ text: "", margin: [0, 0, 0, 8] });
  }

  const docDef: any = {
    pageSize: "A4",
    pageMargins: [50, 60, 50, 60],
    defaultStyle: { font: mainFont, alignment: ALIGN },
    header: () => ({
      text: `${companyName}  —  ${docTitle}`,
      fontSize: 9, color: "#666666", alignment: ALIGN, margin: [50, 20, 50, 0],
    }),
    footer: (currentPage: number, pageCount: number) => ({
      text: `${dateStr}  •  ${isAr ? "صفحة" : "Page"} ${currentPage} / ${pageCount}`,
      fontSize: 9, color: "#888888", alignment: "center", margin: [50, 0, 50, 20],
    }),
    content,
  };

  const pdfDocPromise = printer.createPdfKitDocument(docDef);
  const pdfDoc = await pdfDocPromise;
  const chunks: Buffer[] = [];
  return await new Promise<Buffer>((resolve, reject) => {
    pdfDoc.on("data", (c: Buffer) => chunks.push(c));
    pdfDoc.on("end", () => resolve(Buffer.concat(chunks)));
    pdfDoc.on("error", reject);
    pdfDoc.end();
  });
}
